/**
 * Shared tilt/orientation presets for the PV-forecast dialog (input) and card (display).
 * Keeping a single source of truth ensures a value picked in the dialog renders back
 * as the same named preset on the card.
 */

/** Preset key signalling that the user wants to type an exact value instead of picking a range. */
export const EXACT = 'exact';

export interface PresetOption {
  /** Stable value stored in the select control and matched on edit. */
  key: string;
  /** Degrees sent to the API; `null` for the manual-entry option. Range presets use the midpoint. */
  degrees: number | null;
  labelKey: string;
}

/** Tilt presets are ranges; the stored value is the range midpoint (product decision). */
export const TILT_PRESETS: PresetOption[] = [
  { key: 'flat', degrees: 0, labelKey: 'devices.pvForecast.dialog.tiltOptions.flat' },
  { key: 'small', degrees: 13, labelKey: 'devices.pvForecast.dialog.tiltOptions.small' },
  { key: 'medium', degrees: 23, labelKey: 'devices.pvForecast.dialog.tiltOptions.medium' },
  { key: 'big', degrees: 35, labelKey: 'devices.pvForecast.dialog.tiltOptions.big' },
  { key: EXACT, degrees: null, labelKey: 'devices.pvForecast.dialog.tiltOptions.exact' },
];

/** Degrees clockwise from north (N = 0, E = 90, S = 180, W = 270), the convention the forecast
 *  model reads the stored value in. */
export const AZIMUTH_PRESETS: PresetOption[] = [
  { key: 'south', degrees: 180, labelKey: 'devices.pvForecast.dialog.azimuthOptions.south' },
  { key: 'east', degrees: 90, labelKey: 'devices.pvForecast.dialog.azimuthOptions.east' },
  { key: 'west', degrees: 270, labelKey: 'devices.pvForecast.dialog.azimuthOptions.west' },
  {
    key: 'southEast',
    degrees: 135,
    labelKey: 'devices.pvForecast.dialog.azimuthOptions.southEast',
  },
  {
    key: 'southWest',
    degrees: 225,
    labelKey: 'devices.pvForecast.dialog.azimuthOptions.southWest',
  },
  { key: EXACT, degrees: null, labelKey: 'devices.pvForecast.dialog.azimuthOptions.exact' },
];

/** The preset whose degrees exactly match a stored value, if any (never the `exact` option). */
function matchPreset(presets: PresetOption[], value: number): PresetOption | undefined {
  return presets.find(option => option.degrees !== null && option.degrees === value);
}

/** Resolves the degrees to send: the preset's value, or the typed number for `exact`. */
export function degreesFor(presets: PresetOption[], presetKey: string, exactValue: string): number {
  const preset = presets.find(option => option.key === presetKey);
  return preset && preset.degrees !== null ? preset.degrees : Number(exactValue);
}

/** Maps stored degrees back to the matching preset key, or `exact` when nothing matches. */
export function presetKeyFor(presets: PresetOption[], value: number): string {
  return matchPreset(presets, value)?.key ?? EXACT;
}

/** The i18n label key for a stored value, or `null` when it's a custom (non-preset) value. */
export function presetLabelKeyFor(presets: PresetOption[], value: number): string | null {
  return matchPreset(presets, value)?.labelKey ?? null;
}
